import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CamManageService {
  constructor(private http: HttpClient) {}
  /**
   * 🔴 DC-S-680 — LẤY ĐỦ camera, KHÔNG để E1 cắt ở trang mặc định.
   *
   * Bản cũ gọi `/cameras` TRẦN, không tham số. E1 phân trang với **mặc định size=20** ⇒ FE chỉ
   * nhận **20/27 camera** — 7 cái còn lại **chưa bao giờ được tải về**. Rồi FE lại tự phân trang
   * client-side trên cái danh sách đã cụt, nên nhìn thế nào cũng thấy "đủ trang", không ai nghi.
   *
   * Tệ hơn: badge "20 camera" lấy từ `totalItems` của E1 — mà **`totalItems` KHÔNG phải tổng thật**,
   * nó bằng số phần tử đã trả về (đo: `?size=100` → totalItems=27; `?size=10&page=1` → totalItems=20).
   * Nên con số hiển thị **tự xác nhận chính cái danh sách cụt của nó**. Không có gì mâu thuẫn để
   * người dùng nhận ra — đó là lý do lỗi này sống được lâu.
   *
   * ⇒ Xin size lớn, và **đếm bằng `data.length`**, không tin `totalItems`.
   */
  /**
   * 🔴 DC-S-1478 (2026-09-07) — MẶC ĐỊNH size phải LỚN, không phải 10.
   *
   * Bản đổi hôm nay đặt `size = 10` làm mặc định. Nhưng 7 chỗ đang gọi `getAllCam()` TRẦN
   * (dashboard grid, live-view, cam-fullscreen, schedule-cam, location-hierarchy, cam-map-viewer,
   * navbar-notification) ⇒ tất cả im lặng chỉ nhận 10/143 camera.
   * Đo prod: lưới Dashboard tụt còn 9 ô (10 cam về, 9 ONLINE) ⇒ `totalPages=1` ⇒ **mất nút phân
   * trang** (owner báo). Navbar cũng chỉ soi được 10 cam để tìm camera offline.
   *
   * Chỉ MỘT chỗ thật sự phân trang là màn Quản lý camera — và nó TRUYỀN tham số tường minh
   * (`getAllCam(currentPage, pageSize, search)`), nên không phụ thuộc mặc định.
   * ⇒ Trả mặc định về lớn để 7 chỗ kia đúng lại; ai cần phân trang thì truyền tham số.
   */
  getAllCam(page: number = 0, size: number = 1000, search?: string) {
    // size <= 0 = gọi sai (thường do lẫn thứ tự tham số) → dùng mặc định thay vì
    // gửi size=0 rồi trả danh sách RỖNG mà không báo lỗi gì.
    const safeSize = Number.isFinite(size) && size > 0 ? size : 1000;
    let params = new HttpParams()
      .set('page', String(Number.isFinite(page) && page >= 0 ? page : 0))
      .set('size', String(safeSize));

    if (search && typeof search === 'string' && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<any>(`${environment.apiUrl}/cameras`, { params });
  }

  /** DC-S-580 / POST /api/v1/cameras — register camera vào hệ thống */
  createCamera(body: {
    code?: string;
    camera_id?: string;
    name?: string;
    type?: string;
    deviceType?: string;
    device_type?: string;
    locationId?: number | null;
    lat?: number | null;
    lng?: number | null;
    sceneType?: string;
    scene_type?: string;
    site_id?: string;
    board_rtsp_source?: string;
    plainConfig?: {
      ip?: string;
      onvifPort?: number;
      isapiPort?: number;
      username?: string;
      password?: string;
      provisionHikvision?: boolean;
      rtspMain?: string;
      rtspSub?: string;
    };
    [key: string]: any;
  }) {
    return this.http.post<any>(`${environment.apiUrl}/cameras`, body);
  }
  /** DC-S-594 — ONVIF discover camera thường (device info + profiles + RTSP main/sub) trước khi register. */
  onvifDiscover(body: {
    ip: string;
    port?: number;
    isapi_port?: number;
    user: string;
    pass: string;
    provision_hikvision?: boolean;
  }) {
    return this.http.post(`${environment.apiUrl}/cameras/onvif/discover`, body);
  }
  /**
   * GET /api/v1/cameras/{id}/onvif-device
   * Đọc thông tin phần cứng ONVIF: hãng sản xuất, model, firmware_version, serial_number.
   */
  getOnvifDevice(cameraId: string | number) {
    return this.http.get<any>(`${environment.apiUrl}/cameras/${cameraId}/onvif-device`);
  }
  /** @deprecated Dùng getOnvifDevice thay thế. */
  getPlainOnvifDevice(cameraId: string | number) {
    return this.getOnvifDevice(cameraId);
  }

  /**
   * GET /api/v1/cameras/{id}/onvif-imaging
   * Đọc cấu hình hình ảnh camera: brightness, contrast, sharpness, ir_cut_filter, wdr.
   */
  getOnvifImaging(cameraId: string | number) {
    return this.http.get<any>(`${environment.apiUrl}/cameras/${cameraId}/onvif-imaging`);
  }
  /** @deprecated Dùng getOnvifImaging thay thế. */
  getPlainOnvifImaging(cameraId: string | number) {
    return this.getOnvifImaging(cameraId);
  }

  /**
   * PUT /api/v1/cameras/{id}/onvif-imaging
   * Chỉnh cấu hình hình ảnh camera ONVIF.
   */
  setOnvifImaging(cameraId: string | number, body: any) {
    return this.http.put<any>(`${environment.apiUrl}/cameras/${cameraId}/onvif-imaging`, body);
  }
  /** @deprecated Dùng setOnvifImaging thay thế. */
  setPlainOnvifImaging(cameraId: string | number, body: any) {
    return this.setOnvifImaging(cameraId, body);
  }

  /**
   * GET /api/v1/cameras/next-id?siteId=...
   * Gợi ý mã camera tiếp theo cho site.
   */
  nextCameraId(siteId: string) {
    return this.http.get<any>(`${environment.apiUrl}/cameras/next-id?siteId=${encodeURIComponent(siteId)}`);
  }

  /**
   * GET /api/v1/cameras/id-available?id=...
   * Kiểm tra trùng mã camera trong hệ thống.
   */
  checkCameraId(cameraId: string) {
    return this.http.get<any>(`${environment.apiUrl}/cameras/id-available?id=${encodeURIComponent(cameraId)}`);
  }

  /**
   * GET /api/v1/cameras/offline-summary
   * Thống kê số lượng và danh sách camera OFFLINE mất kết nối.
   */
  getOfflineSummary() {
    return this.http.get<{
      success: boolean;
      status: string;
      data: {
        offlineCount: number;
        cameras: Array<{
          id: number;
          code: string;
          name: string;
          status: string;
          locationName?: string;
          lastSeenAt?: string;
        }>;
        updatedAt?: string;
      };
    }>(`${environment.apiUrl}/cameras/offline-summary`);
  }

  /**
   * GET /api/v1/cameras/{id}
   * Lấy chi tiết 1 camera theo ID số (Long) hoặc Mã Code (String).
   */
  getCameraDetail(cameraId: string | number) {
    return this.http.get<any>(`${environment.apiUrl}/cameras/${cameraId}`);
  }
  /** DC-S-597 — kiểm tra link RTSP có SỐNG không + codec/độ phân giải THẬT (từ SDP).
   *  Dùng cho camera KHÔNG hỗ trợ ONVIF (nhập link tay). Link chết vẫn trả 200 với ok=false. */
  rtspProbe(body: { url: string }) {
    return this.http.post(`${environment.apiUrl}/cameras/rtsp-probe`, body);
  }
  /** DC-S-878 — ONVIF relay QUA board (ai_board_multi): board chạy ONVIF LOCAL tới camera sau NAT.
   *  enqueue → trả {command_id}; poll onvifRelayResult tới khi state=acked. */
  onvifRelay(
    deviceId: string,
    body: { op: string; camera_ip?: string; onvif_port?: number; user?: string; pass?: string; subnet?: string }
  ) {
    return this.http.post<any>(`${environment.apiUrl}/devices/${deviceId}/onvif-relay`, body);
  }
  onvifRelayResult(commandId: string) {
    return this.http.get<any>(`${environment.apiUrl}/onvif-relay/${commandId}`);
  }
  /** DC-S-594/597 — register camera_plain PULL RTSP. 2 chế độ:
   *  ONVIF (ip+user+pass → DC tự discover) HOẶC rtsp_main (nhập tay, bỏ qua ONVIF).
   *  BE probe RTSP thật trước khi ghi DB — link chết thì không đăng ký. */
  plainRegister(body: {
    ip?: string;
    onvif_port?: number;
    isapi_port?: number;
    user?: string;
    pass?: string;
    provision_hikvision?: boolean;
    camera_id?: string;
    name?: string;
    site_id?: string;
    rtsp_main?: string;
    rtsp_sub?: string;
    allow_update?: boolean;
    sub_only?: boolean;
  }) {
    return this.http.post(`${environment.apiUrl}/cameras/plain-register`, body);
  }

  /** DC-S-857 — luồng sub hiện tại của camera_plain (mask credential) cho block "Luồng phụ". */
  getSubStream(cameraId: string) {
    return this.http.get<any>(`${environment.apiUrl}/cameras/${cameraId}/substream`);
  }

  /** DC-S-857 — thêm/đổi/GỠ luồng sub cho camera_plain đã tồn tại (không phải nhập lại main).
   *  Tái dùng plain-register với allow_update + CHỈ rtsp_sub → DC giữ main từ DB, chỉ đổi sub.
   *  sub rỗng = gỡ sub. site_id không cần (DC không đụng site ở nhánh sub-only). */
  updateSubStream(cameraId: string, rtspSub: string) {
    return this.http.post(`${environment.apiUrl}/cameras/plain-register`, {
      camera_id: cameraId,
      rtsp_sub: (rtspSub || '').trim(),
      allow_update: true,
      sub_only: true
    });
  }
  /** DC-S-460 — đổi loại + board (gắn vào board nào) 1 camera (ghi xuống DC). */
  setCameraDeviceType(
    cameraId: string,
    deviceType: string,
    deviceId?: string | null,
    boardRtspSource?: string,
    onvifPort?: number
  ) {
    const body: any = { device_type: deviceType };
    if (deviceId !== undefined) body.device_id = deviceId ?? '';
    // DC-S-876 — ai_board_multi: gửi RTSP board KÉO khi SỬA (trước chỉ set lúc tạo → sửa không lưu).
    if (boardRtspSource !== undefined && boardRtspSource !== null) body.board_rtsp_source = boardRtspSource;
    // DC-S-958 — lưu cổng ONVIF (từ Dò RTSP) để dialog Cấu hình ONVIF (get_encoder qua ONVIF_RELAY)
    // của camera sau board đa-camera tái dùng. Không có port thì get_encoder không biết cổng nào (≠554/80).
    if (onvifPort !== undefined && onvifPort !== null && onvifPort > 0) body.onvif_port = onvifPort;
    return this.http.post(`${environment.apiUrl}/vms/cameras/${cameraId}/device-type`, body);
  }
  /**
   * DC-S-930 — bật/tắt audio luồng live của 1 camera.
   * MẶC ĐỊNH TẮT: audio ăn uplink mọi camera 24/7 dù hầu như không ai nghe.
   * Camera phát G711 cần board transcode→AAC mới có tiếng (response trả kèm `note`).
   */
  setCameraAudio(cameraId: string, enabled: boolean) {
    return this.http.post<any>(`${environment.apiUrl}/vms/cameras/${cameraId}/audio`, { enabled });
  }
  /** DC-S-1145 — bật/tắt GHI HÌNH (central→MinIO + board-local). Tắt = không ghi ở đâu; vẫn stream+AI. */
  setCameraRecord(cameraId: string, enabled: boolean) {
    return this.http.post<any>(`${environment.apiUrl}/vms/cameras/${cameraId}/record`, { enabled });
  }
  /** DC-S-1145 — OFFLINE-CAM: ngắt hẳn camera khỏi board (không pull/push/status/AI). Cần firmware v1.2.12. */
  setCameraOffline(cameraId: string, enabled: boolean) {
    return this.http.post<any>(`${environment.apiUrl}/vms/cameras/${cameraId}/enabled`, { enabled });
  }
  /** DC-S-460 — danh sách board (edge_devices) để gán camera vào. */
  getBoards() {
    return this.http.get<any>(`${environment.apiUrl}/devices`);
  }
  getCam(page: number, size: number) {
    return this.http.get(`${environment.apiUrl}/cameras`, {
      params: {
        page: page.toString(),
        size: size.toString()
      }
    });
  }

  /**
   * GET /api/v1/cameras
   * Lấy danh sách Camera phân trang & lọc theo locationId, type, status.
   */
  getCamList(params?: { locationId?: number; type?: string; status?: string; page?: number; size?: number }) {
    let httpParams = new HttpParams();
    if (params) {
      if (params.locationId != null) httpParams = httpParams.set('locationId', params.locationId.toString());
      if (params.type) httpParams = httpParams.set('type', params.type);
      if (params.status) httpParams = httpParams.set('status', params.status);
      if (params.page != null) httpParams = httpParams.set('page', params.page.toString());
      if (params.size != null) httpParams = httpParams.set('size', params.size.toString());
    }
    return this.http.get<any>(`${environment.apiUrl}/cameras`, { params: httpParams });
  }

  syncCam() {
    return this.http.post(`${environment.apiUrl}/cameras/sync-vms`, {});
  }

  /**
   * DC-S-1015 — danh mục vị trí THẬT (E1 tbl_location, 135 dòng: 1 CITY + các WARD).
   * Trước đây ô "Vị trí" ở form Sửa camera là 5 chuỗi BỊA cứng trong code
   * ("Ngã tư Hai Bà Trưng", "Hàng Bài"…) và giá trị chọn xong KHÔNG được gửi đi đâu cả —
   * ô trang trí. Hệ quả thật: 111/111 camera có `location_id` NULL, nên nhánh phân quyền
   * theo CỤM vị trí (`camera.location.path LIKE acl.location.path || '%'`) không bao giờ
   * khớp — chỉ grant từng-camera-một chạy được.
   */
  getLocations() {
    return this.http.get<any>(`${environment.apiUrl}/locations`);
  }
  getLocationsTree() {
    return this.http.get<any>(`${environment.apiUrl}/locations/tree`);
  }

  /**
   * Unified Camera Update API (PUT /api/v1/cameras/{id}).
   * Hợp nhất việc cập nhật thông tin Camera (tên, vị trí, GPS, IP/RTSP, Board AI, ONVIF...)
   * thay thế cho PATCH /metadata và POST /device-type cũ.
   */
  updateCamera(cameraId: number | string, body: Partial<UpdateCameraRequest>) {
    return this.http.put<any>(`${environment.apiUrl}/cameras/${cameraId}`, body);
  }

  /**
   * @deprecated Sử dụng updateCamera(cameraId, { locationId }) thay thế.
   * DC-S-1015 — gán vị trí cho camera.
   */
  setCameraLocation(cameraId: number, currentName: string, locationId: number | null) {
    return this.updateCamera(cameraId, { name: currentName, locationId, clearLocation: locationId == null });
  }

  // DC-S-914 — Phân quyền camera (ACL). Backend E1 api/v1/acl có sẵn 4 endpoint (grant/revoke/list/
  // effective); FE trước đây KHÔNG có màn nào dùng nên tbl_camera_acl trống. Quyền: VIEW|EXPORT|PTZ|MANAGE.
  // Grant theo cameraId (Long, = CameraItem.id) HOẶC locationId — ở đây làm per-camera.
  aclList() {
    // list trả TẤT CẢ acl active; lọc theo camera ở FE (endpoint chỉ filter ?userParticipantId).
    return this.http.get<any>(`${environment.apiUrl}/acl/cameras`);
  }
  aclGrant(body: { userParticipantId: string; cameraId: number; permission: string; expiresAt?: string | null }) {
    return this.http.post<any>(`${environment.apiUrl}/acl/cameras`, body);
  }
  aclRevoke(aclId: number) {
    return this.http.delete<any>(`${environment.apiUrl}/acl/cameras/${aclId}`);
  }
  // Danh sách user (kèm userParticipantId) trong 1 org — nguồn picker "cấp cho ai". Tái dùng endpoint
  // user-manage đã có: GET /organization/:id/users. Trả UserParticipantSearchResponse {userParticipantId, fullName, email, username}.
  orgUsers(organizationId: string, page = 1, limit = 100) {
    return this.http.get<any>(`${environment.apiUrl}/organization/${organizationId}/users`, {
      params: { page: String(page), limit: String(limit) }
    });
  }
  // Cấp JWK bootstrap token onboard camera (admin) — proxy DC edge-bundle qua E1.
  issueBootstrapToken(cameraId: string, body: { ttl_minutes?: number; cert_ttl_hours?: number }) {
    return this.http.post(`${environment.apiUrl}/cameras/${cameraId}/bootstrap-token`, body || {});
  }
  // Lấy SRT publish credential (admin) — proxy DC publish-credential qua E1. Camera phải đã register.
  getSrtCredential(cameraId: string) {
    return this.http.get(`${environment.apiUrl}/cameras/${cameraId}/srt-credential`);
  }
  getLinkCamLiveFull(cameraId: string) {
    return this.http.get(`${environment.apiUrl}/cameras/${cameraId}/live`);
  }
  getLinkCamLiveSubStream(cameraId: string, type: 'main' | 'sub') {
    return this.http.get(`${environment.apiUrl}/cameras/${cameraId}/live`, {
      params: {
        cameraId: cameraId,
        stream: type
      }
    });
  }
  getMultiCamLiveSubStream(body: { cameraIds: string[]; stream: 'main' | 'sub' }) {
    return this.http.post(`${environment.apiUrl}/cameras/live-batch`, body);
  }
  getPlaybackCam(params: { cameraId: string; from: string; to: string }) {
    return this.http.get(`${environment.apiUrl}/cameras/${params.cameraId}/playback`, { params });
  }
  /** ⚠️ E1-ONLY: xoá bản ghi mirror của E1 theo khoá SỐ của E1. KHÔNG tới DC. */
  disableCam(camId: number) {
    return this.http.delete(`${environment.apiUrl}/cameras/${camId}`);
  }
  /** DC-S-619 — XOÁ camera ở **DC** (nguồn sự thật), theo camera_id (`cam_hn01_0003`).
   *
   *  Trước đây nút Xoá CHỈ gọi `disableCam(cam.id)` = khoá SỐ của E1 ⇒ **KHÔNG BAO GIỜ tới DC**
   *  (đo thật: 0 request DELETE tới control-plane trong 30 ngày). Hệ quả: E1 xoá, DC giữ nguyên
   *  ⇒ MediaMTX kéo RTSP mãi (ăn slot camera), dc-ai-worker vẫn infer + publish event, và
   *  `loadCameraHealth` backfill dựng LẠI hàng từ /cameras/health ⇒ camera "đã xoá" hiện lại.
   *  DC.Delete mới là chỗ gỡ MediaMTX path + cam_state (DC-S-617). */
  /** DC-S-626e — `force`: bỏ qua gác "camera của board đang giữ cert còn hiệu lực" (DC trả 409).
   *  Chỉ dùng khi board đã HỎNG/MẤT. Không có tham số này thì thông báo 409 của DC bảo operator
   *  "dùng ?force=1" mà UI lại **không có cách nào gửi** — một lối thoát chỉ tồn tại trên giấy sẽ
   *  đẩy operator đi curl thẳng vào production. */
  deleteCameraDC(cameraId: string, force = false) {
    const qs = force ? '?force=1' : '';
    return this.http.delete(`${environment.apiUrl}/vms/cameras/${cameraId}${qs}`);
  }
  /** @deprecated Sử dụng updateCamera(cameraId, { name }) thay thế. */
  updateCameraName(cameraId: string, name: string) {
    return this.updateCamera(cameraId, { name });
  }
  importCamFromCSV(body) {
    return this.http.post(`${environment.apiUrl}/cameras/bulk`, body);
  }
  getCameraHealth() {
    return this.http.get(`${environment.apiUrl}/cameras/health`);
  }
  getCameraAiFeatures(cameraId: string) {
    return this.http.get<{
      result: boolean;
      code: number;
      message: string;
      data: {
        behavior: boolean;
        detect: boolean;
        lpr: boolean;
      };
    }>(`${environment.apiUrl}/cameras/${cameraId}/ai-features`);
  }
  /**
   * Bài toán AI đang bật của 1 camera (enabled_tasks) — để màn xem fullscreen HIỂN THỊ read-only (DC-S-337).
   * Đọc từ camera detail DC; trả [] nếu chưa set.
   */
  /** DC-S-632 — TRẢ `null` khi camera CHƯA CẤU HÌNH BAO GIỜ (khác `[]` = đã tắt hết).
   *
   *  Bản cũ `|| []` **gộp null với []** — đúng cái bug đã cắn 5 lần. Ở đây nó nằm trong service
   *  của màn Fullscreen (devices.service đã sửa, file này thì KHÔNG), và hậu quả rất nặng:
   *  **8/8 camera thường prod đang có `enabled_tasks = NULL`** ⇒ `dc-ai-worker` chạy **TẤT CẢ**
   *  (detect + LPR + behavior + **FACE, có lưu embedding sinh trắc**) — mà panel "Bài toán AI
   *  đang chạy" lại hiện **"Camera này chưa bật bài toán AI nào."** cho cả 8 con.
   *  Người vận hành/kiểm toán tin là face/biometrics ĐANG TẮT trong khi hệ đang trích xuất và
   *  lưu vector khuôn mặt. null = chưa cấu hình (chạy hết) · [] = đã tắt hết · [..] = danh sách. */
  getCameraAiTasks(cameraId: string) {
    return this.http.get<any>(`${environment.apiUrl}/cameras/${cameraId}`).pipe(
      map((res): string[] | null => {
        const cam = res?.data?.data ?? res?.data ?? res ?? {};
        const raw = cam.enabled_tasks ?? cam.ai_tasks ?? cam.enabledTasks ?? null;
        return raw == null ? null : (raw as string[]);
      })
    );
  }
  updateCameraAiFeatures(cameraId: string, body: { behavior: boolean; detect: boolean; lpr: boolean }) {
    return this.http.put<{
      result: boolean;
      code: number;
      message: string;
      data: {
        behavior: boolean;
        detect: boolean;
        lpr: boolean;
      };
    }>(`${environment.apiUrl}/cameras/${cameraId}/ai-features`, body);
  }
  getMultiCameraAiFeatures(cameraIds: string[]) {
    let params = new HttpParams();
    cameraIds.forEach(id => {
      params = params.append('cameraIds', id);
    });
    return this.http.get<{
      result: boolean;
      code: number;
      message: string;
      data: {
        [cameraId: string]: {
          behavior: boolean;
          detect: boolean;
          lpr: boolean;
        };
      };
    }>(`${environment.apiUrl}/cameras/ai-features`, { params });
  }

  // ── ONVIF APIs ──────────────────────────────────────────────────────────────

  getOnvifEncoder(cameraId: string) {
    return this.http.get<{
      result: boolean;
      code: number;
      message: string;
      data: Array<{
        token: string;
        encoding: string;
        width: number;
        height: number;
        gov_length: number;
        frame_rate: number;
        bitrate: number;
        cbr: boolean;
      }> | null;
    }>(`${environment.apiUrl}/cameras/${cameraId}/onvif/encoder`);
  }

  /**
   * GET /api/v1/cameras/{id}/encoder-live
   * Đọc thông số Stream thực tế: bitrate_kbps, fps, codec, is_alive, ingest_node.
   */
  getEncoderLive(cameraId: string | number) {
    return this.http.get<{
      result?: boolean;
      success?: boolean;
      code?: number;
      data: any;
    }>(`${environment.apiUrl}/cameras/${cameraId}/encoder-live`);
  }

  /**
   * POST/PUT /api/v1/cameras/{id}/encoder-live
   * Cập nhật thông số encoder luồng trực tiếp.
   */
  setEncoderLive(
    cameraId: string | number,
    body: {
      token?: string;
      width?: number;
      height?: number;
      gov?: number;
      fps?: number;
      bitrate?: number;
      [k: string]: any;
    }
  ) {
    return this.http.post<any>(`${environment.apiUrl}/cameras/${cameraId}/encoder-live`, body);
  }

  /**
   * POST /api/v1/cameras/{id}/encoder-live/apply-template
   * Áp dụng template cấu hình encoder tối ưu.
   */
  applyEncoderTemplate(cameraId: string | number) {
    return this.http.post<any>(`${environment.apiUrl}/cameras/${cameraId}/encoder-live/apply-template`, {});
  }

  setOnvifEncoder(
    cameraId: string,
    body: {
      token: string;
      width: number;
      height: number;
      gov: number;
      fps: number;
      bitrate: number;
    }
  ) {
    return this.http.put<{
      result: boolean;
      code: number;
      message: string;
      data: {
        applied: boolean;
        current: {
          token: string;
          encoding: string;
          width: number;
          height: number;
          frame_rate: number;
          bitrate: number;
        };
      } | null;
    }>(`${environment.apiUrl}/cameras/${cameraId}/onvif/encoder`, body);
  }

  applyOnvifTemplate(cameraId: string) {
    return this.http.post<{
      result: boolean;
      code: number;
      message: string;
      data: Array<{
        token: string;
        key: string;
        need_change: boolean;
        applied: boolean;
      }> | null;
    }>(`${environment.apiUrl}/cameras/${cameraId}/onvif/encoder/apply-template`, {});
  }

  // DC-S-1392 — imaging cho camera sau ai_board_multi đi CHUNG getPlainOnvifImaging/setPlainOnvifImaging
  // (route /onvif-imaging đã được E1 nginx bypass sẵn; DC rẽ theo device_type sang ONVIF_RELAY). KHÔNG
  // endpoint riêng imaging-live vì E1 chưa bypass path mới → 404.

  // ── VMS Core Proxy APIs ──────────────────────────────────────────────────────

  getVmsSites() {
    return this.http.get<VmsSitesResponse>(`${environment.apiUrl}/vms/sites`);
  }

  getVmsSiteById(siteId: string) {
    return this.http.get<VmsSiteDetailResponse>(`${environment.apiUrl}/vms/sites/${siteId}`);
  }

  getVmsCameraById(cameraId: string) {
    return this.http.get<VmsCameraDetailResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}`);
  }

  /** DC-S-031/132 — Push AI runtime config xuống edge board (hot-swap) */
  pushCameraConfig(
    cameraId: string,
    body: Partial<VmsCameraConfigBody>
  ): import('rxjs').Observable<VmsCameraConfigResponse> {
    return this.http.post<VmsCameraConfigResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/config`, body);
  }

  /** DC-S-141 — Xem lịch sử lệnh đã gửi xuống edge cho 1 camera */
  getCameraCommands(
    cameraId: string,
    params?: {
      since?: string;
      action?: string;
      state?: string;
      limit?: number;
    }
  ): import('rxjs').Observable<VmsCameraCommandsResponse> {
    let httpParams = new HttpParams();
    if (params?.since) httpParams = httpParams.set('since', params.since);
    if (params?.action) httpParams = httpParams.set('action', params.action);
    if (params?.state) httpParams = httpParams.set('state', params.state);
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    return this.http.get<VmsCameraCommandsResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/commands`, {
      params: httpParams
    });
  }

  /** DC-S-136 — Tình trạng upload segment (Tier 7 catchup) */
  getCatchupStats(cameraId?: string): import('rxjs').Observable<VmsCatchupStatsResponse> {
    let httpParams = new HttpParams();
    if (cameraId) httpParams = httpParams.set('camera_id', cameraId);
    return this.http.get<VmsCatchupStatsResponse>(`${environment.apiUrl}/vms/catchup/stats`, { params: httpParams });
  }

  /** DC-S-182 — Push firmware hoặc AI model xuống edge */
  pushCameraArtifact(cameraId: string, body: VmsArtifactBody): import('rxjs').Observable<VmsArtifactResponse> {
    return this.http.post<VmsArtifactResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/artifact`, body);
  }

  // ── Camera Lifecycle APIs ─────────────────────────────────────────────────

  /** Thu hồi cert camera (admin) — camera không thể publish stream lên DC nữa */
  revokeCameraCert(cameraId: string) {
    return this.http.post<VmsGenericResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/revoke`, {});
  }

  /** Khôi phục cert camera (admin) — camera publish lại được */
  unrevokeCameraCert(cameraId: string) {
    return this.http.post<VmsGenericResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/unrevoke`, {});
  }

  /** Đổi node ingest cho camera (admin) */
  assignCameraNode(cameraId: string, nodeId: string) {
    return this.http.post<VmsAssignNodeResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/assign`, {
      node_id: nodeId
    });
  }

  /** Lấy nguồn stream camera (RTSP URL, protocol, ingest node) */
  getCameraSource(cameraId: string) {
    return this.http.get<VmsCameraSourceResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/source`);
  }

  /** Đo uplink speed camera */
  runCameraSpeedtest(cameraId: string) {
    return this.http.post<VmsCameraSpeedtestResponse>(`${environment.apiUrl}/vms/cameras/${cameraId}/speedtest`, {});
  }

  // ── Infra / Ops APIs (admin) ──────────────────────────────────────────────

  /** Danh sách ingest nodes */
  getIngestNodes() {
    return this.http
      .get<VmsIngestNodesResponse>(`${environment.apiUrl}/vms/ingest-nodes`)
      .pipe(map(res => this.mapIngestNodesResponse(res)));
  }

  private mapIngestNodesResponse(res: any): VmsIngestNodesResponse {
    if (!res) {
      return { success: false, status: 'ERROR', data: [] };
    }
    const isSuccess = res.success === true || res.result === true || res.code === 200 || res.code === 201;
    if (!isSuccess) {
      return { success: false, status: res.status || 'ERROR', data: [] };
    }

    let rawData: any = res.data;
    if (
      (rawData === undefined || rawData === null) &&
      typeof res.message === 'string' &&
      (res.message.trim().startsWith('{') || res.message.trim().startsWith('['))
    ) {
      try {
        rawData = JSON.parse(res.message);
      } catch (e) {
        console.warn('Failed to parse VMS message JSON in service:', e);
      }
    }

    let items: any[] = [];
    if (rawData) {
      if (Array.isArray(rawData)) {
        items = rawData;
      } else if (rawData.items && Array.isArray(rawData.items)) {
        items = rawData.items;
      } else if (rawData.data && Array.isArray(rawData.data)) {
        items = rawData.data;
      }
    }

    const mappedNodes: VmsIngestNode[] = items.map((item: any) => ({
      node_id: item.node_id || item.id || '',
      srt_endpoint: item.srt_endpoint,
      hls_base: item.hls_base,
      whep_base: item.whep_base,
      api_url: item.api_url,
      capacity: item.capacity,
      healthy: item.healthy,
      last_seen: item.last_seen,
      cameras: item.cameras !== undefined ? item.cameras : 0
    }));

    return {
      success: true,
      status: 'SUCCESS',
      data: mappedNodes
    };
  }

  /** Thêm ingest node mới */
  addIngestNode(id: string, address: string) {
    return this.http.post<VmsAddIngestNodeResponse>(`${environment.apiUrl}/vms/ingest-nodes`, { id, address });
  }

  /** Xóa ingest node */
  deleteIngestNode(nodeId: string) {
    return this.http.delete<VmsGenericResponse>(`${environment.apiUrl}/vms/ingest-nodes/${nodeId}`);
  }

  /** Trigger reconciler — phân phối lại camera */
  triggerReconciler() {
    return this.http.post<VmsReconcilerResponse>(`${environment.apiUrl}/vms/reconciler/run-once`, {});
  }

  /** Dung lượng storage toàn hệ thống */
  getStorageUsage() {
    return this.http.get<VmsStorageUsageResponse>(`${environment.apiUrl}/vms/storage/usage`);
  }
}

export interface VmsSite {
  site_id: string;
  name: string;
  location: any;
  created_at: string;
  updated_at: string;
}

export interface VmsSitesResponse {
  result: boolean;
  code: number;
  message: string;
  data: {
    count: number;
    items: VmsSite[];
  };
}

export interface VmsSiteDetailResponse {
  result: boolean;
  code: number;
  message: string;
  data: VmsSite;
}

export interface VmsCameraDetail {
  camera_id: string;
  name: string;
  site_id: string;
  state: string;
  ingest_node: string;
  fw_version: string;
  hw_model: any;
  created_at: string;
  last_beat_ts: string;
}

export interface VmsCameraDetailResponse {
  result: boolean;
  code: number;
  message: string;
  data: VmsCameraDetail;
}

// ── VMS Config Push ──────────────────────────────────────────────────────────

export interface VmsBandwidthConfig {
  upload_normal_kbps?: number;
  upload_catchup_kbps?: number;
  upload_conservative_kbps?: number;
}

export interface VmsCameraConfigBody {
  lpr_profile?: 'aggressive' | 'conservative';
  min_publish_conf?: number;
  ai_conf_threshold?: number;
  target_fps?: number;
  ingest_endpoint?: string;
  bandwidth?: VmsBandwidthConfig;
}

export interface VmsCameraConfigResponse {
  success?: boolean;
  result?: boolean;
  code?: number;
  message?: string;
  data?: {
    camera_id: string;
    config_version: number;
    command_id: string;
    payload_json: string;
  };
}

// ── Command Trace ────────────────────────────────────────────────────────────

export interface VmsCameraCommand {
  command_id: string;
  action: string;
  state: 'pending' | 'sent' | 'acked' | 'failed' | 'expired';
  priority: number;
  retry_count: number;
  created_at: string;
  sent_at: string | null;
  acked_at: string | null;
  expires_at: string;
  enqueue_to_sent_ms: number | null;
  sent_to_acked_ms: number | null;
  total_dispatch_ms: number | null;
  error_msg: string;
}

export interface VmsCameraCommandsResponse {
  success?: boolean;
  result?: boolean;
  code?: number;
  message?: string;
  data?: VmsCameraCommand[];
}

// ── Catchup Stats ────────────────────────────────────────────────────────────

export interface VmsCatchupStatItem {
  camera_id: string;
  total_segments_indexed_dc: number;
  last_segment_at: string | null;
  total_list_dispatched_24h: number;
  last_list_at: string | null;
  total_force_upload_dispatched_24h: number;
  last_force_upload_at: string | null;
  last_gap_detected_at: string | null;
  pending_commands?: {
    command_id: string;
    action: string;
    state: string;
    enqueued_at: string;
    expires_at: string;
  }[];
}

export interface VmsCatchupStatsResponse {
  success?: boolean;
  result?: boolean;
  code?: number;
  message?: string;
  data?: VmsCatchupStatItem | VmsCatchupStatItem[];
}

// ── Artifact Push ────────────────────────────────────────────────────────────

export interface VmsArtifactBody {
  kind: 'model' | 'firmware';
  object_key: string;
  version: string;
  sha256?: string;
}

export interface VmsArtifactResponse {
  success?: boolean;
  result?: boolean;
  code?: number;
  message?: string;
  data?: {
    command_id: string;
    action: string;
    state: string;
    camera_id: string;
  };
}

// ── Camera Lifecycle Responses ──────────────────────────────────────────────

export interface VmsGenericResponse {
  success?: boolean;
  status?: string;
  data?: any;
}

export interface VmsAssignNodeResponse {
  success?: boolean;
  status?: string;
  data?: {
    camera_id: string;
    node_id: string;
    assigned_at: string;
  };
}

export interface VmsCameraSourceResponse {
  success?: boolean;
  status?: string;
  data?: {
    camera_id: string;
    rtsp_url: string;
    protocol: string;
    stream: string;
    ingest_node: string;
  };
}

export interface VmsCameraSpeedtestResponse {
  success?: boolean;
  status?: string;
  data?: {
    camera_id: string;
    bitrate_kbps: number;
    latency_ms: number;
    tested_at: string;
  };
}

// ── Infra / Ops Responses ───────────────────────────────────────────────────

export interface VmsIngestNode {
  node_id: string;
  srt_endpoint?: string;
  hls_base?: string;
  whep_base?: string;
  api_url?: string;
  capacity?: number;
  healthy?: boolean;
  last_seen?: string;
  cameras?: number; // số camera đang gán node (DC-S-247)
}

export interface VmsIngestNodesResponse {
  success?: boolean;
  status?: string;
  data?: VmsIngestNode[];
}

export interface VmsAddIngestNodeResponse {
  success?: boolean;
  status?: string;
  data?: VmsIngestNode;
}

export interface VmsReconcilerResponse {
  success?: boolean;
  status?: string;
  data?: {
    triggered: boolean;
    triggered_at: string;
    cameras?: number;
    gaps_detected?: number;
    commands_enqueued?: number;
    duration_ms?: number;
    note?: string;
  };
}

export interface VmsStorageNodeUsage {
  node_id: string;
  used_gb: number;
  total_gb: number;
  usage_pct: number;
}

export interface VmsStorageUsageResponse {
  success?: boolean;
  status?: string;
  data?: {
    total_gb: number;
    used_gb: number;
    free_gb: number;
    usage_pct: number;
    retention_days: number;
    is_forecast?: boolean;
    nodes: VmsStorageNodeUsage[];
  };
}

export interface UpdateCameraRequest {
  name?: string;
  locationId?: number | null;
  clearLocation?: boolean;
  lat?: number | null;
  lng?: number | null;
  ip?: string | null;
  rtspUrl?: string | null;
  codec?: string | null;
  resolution?: string | null;
  type?: string | null;
  ptzCapable?: boolean | null;
  metadata?: string | null;
  deviceType?: string | null;
  deviceId?: string | null;
  boardRtspSource?: string | null;
  onvifPort?: number | null;
}

export interface CameraItem {
  id?: number;
  code: string;
  name: string;
  status: string;
  type: string;
  deviceType?: string;
  boardId?: string;
  revoked?: boolean;
  boardRtspSource?: string;
  boardOnvifPort?: number;
  locationName: string;
  ip: string;
  codec: string;
  resolution: string;
  ptzCapable: boolean;
  rtspUrl: string;
  locationId?: number;
  lat?: number;
  lng?: number;
  health?: any;
  resources?: { [k: string]: number };
  aiFeatures?: {
    behavior: boolean;
    detect: boolean;
    lpr: boolean;
  };
}
